import React, { useState } from 'react';
import { Form, notification, Button as ANTButton } from 'antd';
import Input from 'components/Atom/Input';
import { Button } from 'components/Atom/Button';
import http from 'utils/http';

export const EditBilling = ({ handleOpen, updateState, store }) => {
    const [loading, setLoading] = useState(false);
    const billingDetails = store?.billingDetails || {};

    const initialValues = {
      bank_name: billingDetails?.bankName || '',
      bank_branch: billingDetails?.bankBranch || '',
      account_no: billingDetails?.accountNumber || '',
      iban_no: billingDetails?.iban || '',
      swift_code: billingDetails?.swiftCode || '',
      ntn_no: billingDetails?.ntn || '',
      sales_tax_no: billingDetails?.salesTaxNumber || '',
      address: billingDetails?.address || '',
      email: billingDetails?.email || '',
      phone_number: billingDetails?.phoneNumber || '',
    };

    const onSubmit = async (values) => {
      try {
        setLoading(true);
        const payload = {
          billingDetails: {
            bankName: values.bank_name,
            bankBranch: values.bank_branch,
            accountNumber: values.account_no,
            iban: values.iban_no,
            swiftCode: values.swift_code,
            ntn: values.ntn_no,
            salesTaxNumber: values.sales_tax_no,
            address: values.address,
            email: values.email,
            phoneNumber: values.phone_number,
          },
        };

        const res = await http.patch(`/stores/${store?._id}`, payload);

        if (res?.data?.success) {
          notification.success({
            message: 'Billing settings updated successfully',
            placement: 'bottomRight',
          });
          updateState();
          handleOpen();
        }
        setLoading(false);
      } catch (err) {
        setLoading(false);
        notification.error({
          message: err?.response?.data?.message ?? 'Failed to update billing settings',
          placement: 'bottomRight',
        });
      }
    };

    const customValidator = {
      validator: (_, value) => {
        if (!value || value?.trim?.()?.length === 0) {
          return Promise.reject(new Error('Only spaces are not allowed'));
        }

        // Detect URLs: http, https, www, or domain.tld
        const urlRegex = /(https?:\/\/|www\.|[a-zA-Z0-9-]+\.[a-zA-Z]{2,})/i;

        if (urlRegex.test(value)) {
          return Promise.reject(new Error('URLs or website addresses are not allowed'));
        }

        return Promise.resolve();
      },
    };

    return (
      <Form layout='vertical' initialValues={initialValues} onFinish={onSubmit} method='post'>
        <Form.Item
          name='bank_name'
          label='Bank Name'
          className='modal-field'
          rules={[
            { required: true, message: 'Bank name is required' },
            { min: 3, message: 'Minimum 3 characters required' },
            { max: 50, message: 'Maximum 50 characters allowed' },
            {
              pattern: /^[A-Za-z\s]+$/,
              message: 'Only alphabets are allowed',
            },
            customValidator,
          ]}>
          <Input placeholder='Enter bank name' name='bank_name' />
        </Form.Item>

        <Form.Item
          name='bank_branch'
          label='Bank Branch'
          className='modal-field'
          rules={[
            { required: true, message: 'Bank branch is required' },
            { min: 3, message: 'Minimum 3 characters required' },
            { max: 100, message: 'Maximum 100 characters allowed' },
            customValidator,
          ]}>
          <Input placeholder='Enter bank branch' name='bank_branch' />
        </Form.Item>

        <Form.Item
          name='account_no'
          label='Account No'
          className='modal-field'
          rules={[
            { required: true, message: 'Account number is required' },
            { pattern: /^[0-9]+$/, message: 'Only numbers are allowed' },
            { min: 8, message: 'Minimum 8 characters required' },
            { max: 20, message: 'Maximum 20 characters allowed' },
            customValidator,
          ]}>
          <Input placeholder='Enter account number' name='account_no' />
        </Form.Item>

        <Form.Item
          name='iban_no'
          label='IBAN No'
          className='modal-field'
          rules={[
            { required: true, message: 'IBAN number is required' },
            {
              pattern: /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/,
              message: 'Invalid IBAN format',
            },
            { min: 15, message: 'Minimum 15 characters required' },
            { max: 34, message: 'Maximum 34 characters allowed' },
            customValidator,
          ]}>
          <Input placeholder='Enter IBAN number' name='iban_no' />
        </Form.Item>

        <Form.Item
          name='swift_code'
          label='SWIFT Code'
          className='modal-field'
          rules={[
            { required: true, message: 'SWIFT code is required' },
            {
              pattern: /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/,
              message: 'Invalid SWIFT code',
            },
            { min: 8, message: 'Minimum 8 characters required' },
            { max: 11, message: 'Maximum 11 characters allowed' },
            customValidator,
          ]}>
          <Input placeholder='Enter SWIFT code' name='swift_code' />
        </Form.Item>

        <Form.Item
          name='ntn_no'
          label='NTN No'
          className='modal-field'
          rules={[
            { required: true, message: 'NTN number is required' },
            {
              pattern: /^(\d{7}|\d{7}-\d)$/,
              message: 'Invalid NTN format',
            },
            { min: 7, message: 'Minimum 7 characters required' },
            { max: 15, message: 'Maximum 15 characters allowed' },
            customValidator,
          ]}>
          <Input placeholder='Enter NTN number' name='ntn_no' />
        </Form.Item>

        <Form.Item
          name='sales_tax_no'
          label='Sales Tax No'
          className='modal-field'
          rules={[
            { required: true, message: 'Sales tax number is required' },
            {
              pattern: /^\d{15}$/,
              message: 'Sales Tax number must be 15 digits',
            },
            { min: 8, message: 'Minimum 8 characters required' },
            { max: 20, message: 'Maximum 20 characters allowed' },
            customValidator,
          ]}>
          <Input placeholder='Enter sales tax number' name='sales_tax_no' />
        </Form.Item>

        <Form.Item
          name='address'
          label='Address Details'
          className='modal-field'
          rules={[
            { required: true, message: 'Address is required' },
            { min: 10, message: 'Minimum 10 characters required' },
            { max: 200, message: 'Maximum 200 characters allowed' },
            customValidator,
          ]}>
          <Input placeholder='Enter complete address' name='address' />
        </Form.Item>

        <Form.Item
          name='email'
          label='Email'
          className='modal-field'
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email', message: 'Enter a valid email address' },
          ]}>
          <Input placeholder='Enter billing email' name='email' />
        </Form.Item>

        <Form.Item
          name='phone_number'
          label='Phone Number'
          className='modal-field'
          rules={[
            {
              required: true,
              message: 'Phone number is required',
            },
            { pattern: /^\+?[0-9]{10,15}$/, message: 'Enter a valid phone number (10-15 digits, optional + at the start)' },
          ]}>
          <Input placeholder='Enter phone number (e.g., +92 300 1234567)' name='phone_number' />
        </Form.Item>

        <Form.Item>
          <div className='flex justify-end gap-2'>
            <ANTButton className='rounded-lg' block onClick={() => handleOpen()} disabled={loading}>
              Cancel
            </ANTButton>
            <Button type='primary' block className='primaryBtn ml-4' htmlType='submit' loading={loading}>
              Update Billing Settings
            </Button>
          </div>
        </Form.Item>
      </Form>
    );
};
